// React and MobX
import React from "react";
import { useObserver } from "mobx-react-lite";

// Material-UI Core and Styles
import { Box, IconButton, Typography } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { Close as CloseIcon } from "@material-ui/icons";

// Core
import { useRouter } from "Core/Base";
import { isEmptyOrWhitespace } from "Core/Utils/Utils";

// Custom Components
import { NavButton } from "Custom/Components/Buttons/NavButton";
import Lightbox from "Custom/Components/Popups/Lightbox";
import { PrivateBlob } from "Custom/Components/Image/PrivateImage";

// Custom Styles
import { DashboardPageStyles, MainPageStyles, usePageAnimationStyles } from "Custom/StylesAppSpecific/DashboardPagesStyling";
import { ViewSnagStyle } from "Custom/StylesAppSpecific/DashboardSubViews/ViewSnagStyling";

// Custom ViewModels
import { ViewSnagViewModel } from "Custom/ViewModels/DashboardSubViewModels/ViewSnagViewModel";

// Enums and Models
import { SnagPhotoModelDTO } from "Custom/Models/DashboardModels/SnagModel";

// Custom Content
import { ReactComponent as SquareLeftArrow } from "../../Content/play-backward-icon.svg";

interface IProps {
    propertyId: string;
    snagId: string;
    onDrawerClose: void;
}

export const ViewSnag: React.FC<any> = props => {
    const dashboardPageStyles = DashboardPageStyles();
    const pageAnimationStyles = usePageAnimationStyles();
    const mainPageStyles = MainPageStyles();
    const viewSnagStyle = ViewSnagStyle();

    const router = useRouter();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("md")) ? true : false;

    const snagId = (router.match.params as any).snagId ? (router.match.params as any).snagId : props.snagId;
    const propertyId = (router.match.params as any).propertyId;

    const [viewModel] = React.useState(() => new ViewSnagViewModel(propertyId, snagId));

    const [lightboxImg, setLightboxImg] = React.useState("");
    const [lightboxImgName, setLightboxImgName] = React.useState("");
    const [lightboxOpen, setLightboxOpen] = React.useState(false);

    /**
     * Create the contents of the title for the desktop.
     */
    // const desktopSnagTitle = () => {
    //     const parts: string[] = [];

    //     parts.push(viewModel.model.snagNumber);

    //     if (!isEmptyOrWhitespace(viewModel.getAreaName)) {
    //         parts.push(viewModel.getAreaName);
    //     }

    //     if (!isEmptyOrWhitespace(viewModel.getTitle)) {
    //         parts.push(viewModel.getTitle);
    //     }

    //     return parts.join(" - ");
    // };

    /**
     * Create the contents of the title for the mobile.
     */
    // const mobileSnagTitle = () => {
    //     const parts: string[] = [];

    //     parts.push(viewModel.model.snagNumber);

    //     if (!isEmptyOrWhitespace(viewModel.getAreaName)) {
    //         parts.push(viewModel.getAreaName);
    //     }

    //     return parts.join(" - ");
    // };

    /**
     * Create the contents of the details. If none are available, display
     * something to that effect.
     */
    const detailsDescription = () => {
        let description = viewModel.model.details;

        if (isEmptyOrWhitespace(description)) {
            description = "None specified.";
        }

        return description;
    };

    const actionDescription = () => {
        let description = viewModel.model.actionTaken;

        if (isEmptyOrWhitespace(description)) {
            description = "None specified.";
        }

        return description;
    };

    const canDisplayThumbnails = () => {
        return viewModel.getSnagPhotos.length > 0;
    };

    const getSnagDateClasses = (viewSnagViewModel: ViewSnagViewModel): string => {
        let classes = viewSnagStyle.date;

        if (viewSnagViewModel.isCompleted) {
            classes = `${classes} ${viewSnagStyle.resolvedSnagTitle}`;
        } else if (viewSnagViewModel.isRejected) {
            classes = `${classes} ${viewSnagStyle.rejectedSnagTitle}`;
        }

        return classes;
    };

    const renderBackToSnags = () =>
        isDesktop ? (
            <Box className={viewSnagStyle.backToSnags}>
                <SquareLeftArrow className="arrowIcon" onClick={() => viewModel.naviagateToSnagList()} style={{ cursor: "pointer" }} />
                <NavButton displayName="Back to Snags" />
            </Box>
        ) : null;

    /**
     * Renders the snag header. This varies between mobile and desktop
     * devices.
     */
    const renderFormHeader = () => {
        return (
            <>
                <Box className={`baseHeader ${viewSnagStyle.sectionHeader}`}>
                    <Box className={viewSnagStyle.mobileHeaderContent}>
                        <Typography component="h4">{viewModel.NumberAndArea}</Typography>
                        <Typography component="p">{viewModel.getTitle}</Typography>
                    </Box>
                    {!isDesktop && (
                        <IconButton onClick={props.onDrawerClose}>
                            <CloseIcon />
                        </IconButton>
                    )}
                </Box>
            </>
        );
    };

    const renderReviewHeader = () => {
        return (
            <Box className={viewSnagStyle.snagStatusBox}>
                {viewModel.isApproved && (
                    <Box className={viewSnagStyle.statusBanner} style={{ borderColor: viewModel.snagStatusBackground }}>
                        <Box className={viewSnagStyle.statusContent}>
                            <Typography component="h4" style={{ color: viewModel.snagStatusBackground }}>
                                {viewModel.snagStatusText}
                            </Typography>

                            <Typography component="p" style={{ color: viewModel.snagStatusBackground }}>
                                {viewModel.approvedNotes}
                            </Typography>
                        </Box>
                    </Box>
                )}

                {viewModel.isRejected && (
                    <Box className={viewSnagStyle.statusBanner} style={{ borderColor: viewModel.snagStatusBackground }}>
                        <Box className={viewSnagStyle.statusContent}>
                            <Typography component="h4" style={{ color: viewModel.snagStatusBackground }}>
                                {viewModel.snagStatusText}
                            </Typography>

                            <Typography component="p" style={{ color: viewModel.snagStatusBackground }}>
                                {viewModel.rejectedReason}
                            </Typography>
                        </Box>
                    </Box>
                )}

                {viewModel.isReviewed && (
                    <Box className={viewSnagStyle.statusBanner} style={{ borderColor: viewModel.snagStatusBackground }}>
                        <Box className={viewSnagStyle.statusContent}>
                            <Typography component="h4" style={{ color: viewModel.snagStatusBackground }}>
                                {viewModel.snagStatusText}
                            </Typography>

                            <Typography component="p" style={{ color: viewModel.snagStatusBackground }}>
                                {viewModel.reviewReason}
                            </Typography>
                        </Box>
                    </Box>
                )}

                {viewModel.reviewCompletedDateFormatted && (
                    <Box className={viewSnagStyle.statusBanner} style={{ borderColor: "#646464" }}>
                        <Box className={viewSnagStyle.statusContent}>
                            <Typography component="h4" style={{ color: "#646464" }}>
                                {viewModel.snagReviewedText}
                            </Typography>

                            <Typography component="p" style={{ color: "#646464" }}>
                                {viewModel.reviewReason}
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Box>
        );
    };

    const renderSnagDetailsAndActions = () => {
        return (
            <Box className={viewSnagStyle.snagDetailsContainer}>
                <Box className={viewSnagStyle.snagStatusContainer}>
                    {viewModel.snagStatusText && renderReviewHeader()}
                    <Box className={viewSnagStyle.snagDetailBox}>
                        <Box className={viewSnagStyle.header}>
                            {/* Snag Header Section */}
                            <Box className={viewSnagStyle.headerRowB}>
                                <Box className={viewSnagStyle.dateContainer}>
                                    <Box className={viewSnagStyle.date}>
                                        <Typography variant="h5">SNAG REPORTED: {viewModel.ReportedDate}</Typography>
                                    </Box>
                                </Box>
                            </Box>

                            {/* Action Details Section */}

                            <Box className={viewSnagStyle.headerRowD}>
                                <Box className={viewSnagStyle.detailsTitle}>
                                    <Typography variant="h5">Details</Typography>
                                </Box>
                                <Box className={viewSnagStyle.details}>
                                    <Typography>{detailsDescription()}</Typography>
                                </Box>
                            </Box>
                        </Box>

                        {/* Render Attached Images */}
                        {renderAttatchedImagesRow()}
                    </Box>
                </Box>
                {viewModel.isCompleted && (
                    <Box className={viewSnagStyle.snagDetailBox}>
                        <Box className={viewSnagStyle.header}>
                            {/* Snag Header Section */}
                            <Box className={viewSnagStyle.headerRowB}>
                                <Box className={viewSnagStyle.dateContainer}>
                                    {viewModel.FormattedSnagStatus !== "" && (
                                        <Box className={getSnagDateClasses(viewModel)}>
                                            <Typography variant="h5">{viewModel.FormattedSnagStatus}</Typography>
                                        </Box>
                                    )}
                                    {viewModel.isCompleted && (
                                        <Box className={viewSnagStyle.completedBy}>
                                            <Typography variant="h5">
                                                <strong>WORK COMPLETED BY:</strong> {viewModel.resolvedBy}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>

                            {/* Rejected Reason Section */}
                            {viewModel.hasRejectedReason && viewModel.isRejected && (
                                <Box className={viewSnagStyle.headerRowC}>
                                    <Box className={viewSnagStyle.rejectedReason}>
                                        <Typography>{viewModel.rejectedReason}</Typography>
                                    </Box>
                                </Box>
                            )}

                            {/* Action Details Section */}
                            {viewModel.isCompleted && (
                                <Box className={viewSnagStyle.headerRowD}>
                                    <Box className={viewSnagStyle.detailsTitle}>
                                        <Typography variant="h5">Action Taken</Typography>
                                    </Box>
                                    <Box className={viewSnagStyle.details}>
                                        <Typography>{actionDescription()}</Typography>
                                    </Box>
                                </Box>
                            )}
                        </Box>

                        {/* Render Attached Images */}
                        {!viewModel.isRejected && renderAttatchedImagesRow(true)}
                    </Box>
                )}
            </Box>
        );
    };

    const renderAttatchedImagesRow = (isCompletedSnag: boolean = false) => {
        let snagPhotos = viewModel.getSnagPhotos;

        if (isCompletedSnag) {
            snagPhotos = snagPhotos.filter(p => p.resolvedImage);
        } else {
            snagPhotos = snagPhotos.filter(p => !p.resolvedImage);
        }

        return (
            <Box className={viewSnagStyle.paddedBox}>
                <Box className={viewSnagStyle.dashedBorderBox}>
                    <Typography variant="h5">Attatched images</Typography>
                    {canDisplayThumbnails() && (
                        <Box className={viewSnagStyle.attechedImagesRow}>
                            {snagPhotos.map((photo: SnagPhotoModelDTO, index: number) => {
                                return (
                                    <Box key={`snag-photo-${index}`}>
                                        <PrivateBlob
                                            src={photo.azureFileName}
                                            alt={`Snag photo ${index}`}
                                            onClick={() => {
                                                setLightboxImg(photo.azureFileName);
                                                setLightboxImgName(photo.fileName);
                                                setLightboxOpen(true);
                                            }}
                                        />
                                        <Typography variant="body2">{photo.fileName}</Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                    {!canDisplayThumbnails() && <Typography variant="body1">No attatched images found.</Typography>}
                </Box>
            </Box>
        );
    };

    return useObserver(() => (
        <Box className={`${dashboardPageStyles.root} ${viewSnagStyle.root} ${pageAnimationStyles.pageWrapper}`}>
            <Box className={`${dashboardPageStyles.workspace} ${mainPageStyles.workspace}`}>
                <React.Fragment>
                    {renderBackToSnags()}
                    {renderFormHeader()}
                    {renderSnagDetailsAndActions()}
                    {/* {viewModel.FormattedSnagStatus !== "" && viewModel.isCompleted && renderSnagActionDetails()} */}
                </React.Fragment>
                <Lightbox isOpen={lightboxOpen} imageUrl={lightboxImg} onClose={setLightboxOpen} fileName={lightboxImgName} />
            </Box>
        </Box>
    ));
};
